/**
 * Sincronización de la cola de salida.
 *
 * El problema que resuelve: el POS cobra, la red se cae antes de que la petición
 * llegue, y el cajero tiene un cliente delante que ya se va. **La venta no puede
 * perderse.** Se guarda en IndexedDB con su clave de idempotencia y se envía
 * cuando vuelva la red.
 *
 * Reglas de la reconciliación:
 *
 * * Se envía **en orden** y de una en una. Enviarlas en paralelo no acelera nada
 *   (son unas pocas) y complica el orden de los folios.
 * * Si el servidor responde con un error de negocio (422, 409), la entrada queda
 *   **rechazada** y se muestra al cajero: reintentarla sola no arreglaría nada.
 * * Si es un problema de red o un 5xx, se queda **pendiente** y se reintenta.
 * * La clave de idempotencia viaja con la entrada, así que un reintento después
 *   de una respuesta perdida devuelve la venta original en vez de cobrar otra.
 */

import { ApiError, apiRequestWithHeaders, isOnline } from "../api";
import {
  db,
  enqueue,
  nextLocalNumber,
  pendingEntries,
  type OutboxEntry,
  type OutboxKind,
  type RefundCreate,
  type RefundPublic,
  type SaleCreate,
  type SalePublic,
} from "./db";

export interface QueueResult {
  entry: OutboxEntry;
  synced: boolean;
}

/** Encola una operación que mueve dinero. */
export async function queueOperation(
  kind: OutboxKind,
  payload: SaleCreate | RefundCreate | { sale_id: string; reason: string },
  idempotencyKey: string,
  path: string,
): Promise<OutboxEntry> {
  return enqueue({
    id: crypto.randomUUID(),
    kind,
    payload,
    idempotencyKey,
    localNumber: await nextLocalNumber(),
    path,
  });
}

async function sendEntry(entry: OutboxEntry): Promise<OutboxEntry> {
  await db.outbox.update(entry.id, { status: "enviando", attempts: entry.attempts + 1 });

  try {
    const { data } = await apiRequestWithHeaders<SalePublic | RefundPublic>(entry.path, {
      method: "POST",
      body: entry.payload,
      idempotencyKey: entry.idempotencyKey,
    });
    const updated: OutboxEntry = { ...entry, status: "enviada", response: data, lastError: null };
    await db.outbox.put(updated);
    return updated;
  } catch (error) {
    const apiError = error instanceof ApiError ? error : null;

    // Un error de negocio no se arregla reintentando: lo tiene que ver una persona.
    const rejected = apiError !== null && !apiError.isTransient && apiError.status < 500;
    const updated: OutboxEntry = {
      ...entry,
      status: rejected ? "rechazada" : "pendiente",
      lastError: apiError?.message ?? (error instanceof Error ? error.message : "Error desconocido"),
    };
    await db.outbox.put(updated);
    return updated;
  }
}

/**
 * Intenta vaciar la cola. Devuelve cuántas se sincronizaron.
 *
 * Se detiene en la primera que no puede enviarse: si la red se cayó, seguir
 * intentando sólo genera ruido.
 */
export async function syncOutbox(): Promise<number> {
  if (!isOnline()) return 0;

  let synced = 0;
  for (const entry of await pendingEntries()) {
    const result = await sendEntry(entry);
    if (result.status === "enviada") {
      synced += 1;
      continue;
    }
    break;
  }
  return synced;
}

/**
 * Cobra una venta: intenta el servidor y, si no hay red, la encola.
 *
 * Devuelve la venta del servidor o la entrada encolada. La interfaz muestra el
 * folio real cuando lo hay y el número local mientras tanto.
 */
export async function submitSale(
  payload: SaleCreate,
  idempotencyKey: string,
): Promise<{ sale: SalePublic | null; queued: OutboxEntry | null }> {
  if (isOnline()) {
    try {
      const { data } = await apiRequestWithHeaders<SalePublic>("/api/v1/sales", {
        method: "POST",
        body: payload,
        idempotencyKey,
      });
      return { sale: data, queued: null };
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      // Un rechazo del servidor se muestra tal cual: encolarlo escondería el error.
      if (apiError !== null && !apiError.isTransient) throw error;
      // Sin red o servidor caído: se encola y se sigue vendiendo.
    }
  }

  const entry = await queueOperation("sale", payload, idempotencyKey, "/api/v1/sales");
  return { sale: null, queued: entry };
}

export async function submitRefund(
  payload: RefundCreate,
  idempotencyKey: string,
): Promise<{ refund: RefundPublic | null; queued: OutboxEntry | null }> {
  if (isOnline()) {
    try {
      const { data } = await apiRequestWithHeaders<RefundPublic>("/api/v1/refunds", {
        method: "POST",
        body: payload,
        idempotencyKey,
      });
      return { refund: data, queued: null };
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      if (apiError !== null && !apiError.isTransient) throw error;
    }
  }

  const entry = await queueOperation("refund", payload, idempotencyKey, "/api/v1/refunds");
  return { refund: null, queued: entry };
}
